import {BaseAlert, BaseBadge, BaseButton, BaseModal} from "@/components";
import {useCreateBlacklistEntry} from "@/hooks/useBlacklist";
import {useCopyToClipboard} from "@/hooks/useCopyToClipboard";
import {useDeleteUser, useResetUserPassword, useToggleUserStatus, useUpdateUser} from "@/hooks/useUsers";
import {useResetWalletPin} from "@/hooks/useWalletPin";
import type {AccountStatus, KycStatus, User} from "@/types/user.types";
import {
  Ban,
  Calendar,
  Check,
  CheckCircle,
  Copy,
  Edit3,
  Globe,
  KeyRound,
  Lock,
  Mail,
  Pause,
  Phone,
  Play,
  Shield,
  Trash2,
} from "lucide-react";
import {useState} from "react";

interface UserDetailModalProps {
  user: User;
  open: boolean;
  onClose: () => void;
}

type ModalView = "detail" | "edit" | "blacklist" | "confirm-delete";

/* ── Helpers ───────────────────────────────────────────────── */

const kycVariant: Record<KycStatus, "success" | "danger" | "warning" | "info" | "neutral"> = {
  approved: "success",
  pending: "warning",
  in_progress: "info",
  failed: "danger",
  rejected: "danger",
  expired: "neutral",
};

const kycLabel: Record<KycStatus, string> = {
  approved: "Aprobado",
  pending: "Pendiente",
  in_progress: "En proceso",
  failed: "Fallido",
  rejected: "Rechazado",
  expired: "Expirado",
};

const statusVariant: Record<AccountStatus, "success" | "danger" | "warning"> = {
  active: "success",
  suspended: "warning",
  blocked: "danger",
};

const statusLabel: Record<AccountStatus, string> = {
  active: "Activo",
  suspended: "Suspendido",
  blocked: "Bloqueado",
};

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-CL", {dateStyle: "medium", timeStyle: "short"}).format(new Date(iso));
};

/* ── Component ─────────────────────────────────────────────── */

export function UserDetailModal({user, open, onClose}: UserDetailModalProps) {
  const [view, setView] = useState<ModalView>("detail");
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({open: false, title: "", description: "", onConfirm: () => {}});

  const toggleStatus = useToggleUserStatus();
  const resetPassword = useResetUserPassword();
  const deleteUser = useDeleteUser();
  const updateUser = useUpdateUser();
  const createBlacklist = useCreateBlacklistEntry();
  const resetPin = useResetWalletPin();

  const showSuccess = (msg: string) => {
    setActionSuccess(msg);
    setTimeout(() => setActionSuccess(null), 3000);
  };

  const handleToggleStatus = () => {
    toggleStatus.mutate(
      {userId: user.id, enabled: user.accountStatus !== "active"},
      {
        onSuccess: () => showSuccess(user.accountStatus === "active" ? "Usuario pausado" : "Usuario activado"),
      }
    );
  };

  const handleResetPassword = () => {
    setAlertConfig({
      open: true,
      title: "Resetear contraseña",
      description: `¿Resetear contraseña de ${user.email}?`,
      onConfirm: () => {
        setAlertConfig(prev => ({...prev, open: false}));
        resetPassword.mutate(user.id, {
          onSuccess: () => showSuccess("Contraseña reseteada"),
        });
      },
    });
  };

  const handleResetPin = () => {
    setAlertConfig({
      open: true,
      title: "Resetear PIN de wallet",
      description: `¿Resetear PIN de wallet de ${user.email}?`,
      onConfirm: () => {
        setAlertConfig(prev => ({...prev, open: false}));
        resetPin.mutate(user.id, {
          onSuccess: () => showSuccess("PIN de wallet reseteado"),
        });
      },
    });
  };

  const handleDelete = () => {
    deleteUser.mutate(user.id, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const handleClose = () => {
    setView("detail");
    setActionSuccess(null);
    onClose();
  };

  return (
    <>
      <BaseModal
        open={open}
        onClose={handleClose}
        title="Detalle de Usuario"
        size="lg"
      >
        {/* Success toast */}
        {actionSuccess && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-(--color-success-bg) text-(--color-success-text) text-sm font-medium flex items-center gap-2 border border-(--color-success)">
            <CheckCircle size={16} />
            {actionSuccess}
          </div>
        )}

        {view === "detail" && (
          <DetailView
            user={user}
            onEdit={() => setView("edit")}
            onBlacklist={() => setView("blacklist")}
            onDelete={() => setView("confirm-delete")}
            onToggleStatus={handleToggleStatus}
            onResetPassword={handleResetPassword}
            onResetPin={handleResetPin}
            isToggling={toggleStatus.isPending}
            isResettingPassword={resetPassword.isPending}
            isResettingPin={resetPin.isPending}
          />
        )}

        {view === "edit" && (
          <EditView
            user={user}
            onBack={() => setView("detail")}
            updateUser={updateUser}
            onSuccess={() => {
              showSuccess("Usuario actualizado");
              setView("detail");
            }}
          />
        )}

        {view === "blacklist" && (
          <BlacklistView
            user={user}
            onBack={() => setView("detail")}
            createBlacklist={createBlacklist}
            onSuccess={() => {
              showSuccess("Agregado a blacklist");
              setView("detail");
            }}
          />
        )}

        {view === "confirm-delete" && (
          <ConfirmDeleteView
            user={user}
            onBack={() => setView("detail")}
            onConfirm={handleDelete}
            isDeleting={deleteUser.isPending}
          />
        )}
      </BaseModal>

      <BaseAlert
        open={alertConfig.open}
        onClose={() => setAlertConfig(prev => ({...prev, open: false}))}
        onConfirm={alertConfig.onConfirm}
        variant="confirm"
        title={alertConfig.title}
        description={alertConfig.description}
      />
    </>
  );
}

/* ── Detail View ───────────────────────────────────────────── */

interface DetailViewProps {
  user: User;
  onEdit: () => void;
  onBlacklist: () => void;
  onDelete: () => void;
  onToggleStatus: () => void;
  onResetPassword: () => void;
  onResetPin: () => void;
  isToggling: boolean;
  isResettingPassword: boolean;
  isResettingPin: boolean;
}

function DetailView({
  user,
  onEdit,
  onBlacklist,
  onDelete,
  onToggleStatus,
  onResetPassword,
  onResetPin,
  isToggling,
  isResettingPassword,
  isResettingPin,
}: DetailViewProps) {
  return (
    <div className="flex flex-col gap-5">
      {/* User header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-(--color-primary) flex items-center justify-center text-white text-xl font-bold shrink-0">
          {user.fullName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-(--color-text-primary) truncate">{user.fullName}</h3>
          <p className="text-sm text-(--color-text-secondary) truncate">{user.email}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <BaseBadge
              variant={statusVariant[user.accountStatus]}
              label={statusLabel[user.accountStatus]}
              dot
            />
            <BaseBadge
              variant={kycVariant[user.kycStatus]}
              label={`KYC: ${kycLabel[user.kycStatus]}`}
              dot
            />
          </div>
        </div>
      </div>

      {/* User ID */}
      <CopyableField
        label="User ID"
        value={user.id}
      />

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-3">
        <InfoItem
          icon={<Mail size={14} />}
          label="Email"
          value={user.email}
        />
        <InfoItem
          icon={<Phone size={14} />}
          label="Teléfono"
          value={user.phone ?? "—"}
        />
        <InfoItem
          icon={<Globe size={14} />}
          label="País"
          value={user.country}
        />
        <InfoItem
          icon={<Shield size={14} />}
          label="Nivel KYC"
          value={user.kycTier}
        />
        <InfoItem
          icon={<Calendar size={14} />}
          label="Registro"
          value={formatDate(user.createdAt)}
        />
        <InfoItem
          icon={<Calendar size={14} />}
          label="Último login"
          value={user.lastLoginAt ? formatDate(user.lastLoginAt) : "—"}
        />
      </div>

      {/* Divider */}
      <div className="border-t border-(--color-border)" />

      {/* Actions */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-(--color-text-secondary) uppercase tracking-wide">Acciones</p>
        <div className="grid grid-cols-2 gap-2">
          <ActionButton
            icon={<Edit3 size={15} />}
            label="Editar usuario"
            onClick={onEdit}
          />
          <ActionButton
            icon={user.accountStatus === "active" ? <Pause size={15} /> : <Play size={15} />}
            label={user.accountStatus === "active" ? "Pausar usuario" : "Activar usuario"}
            onClick={onToggleStatus}
            loading={isToggling}
            variant={user.accountStatus === "active" ? "warning" : "success"}
          />
          <ActionButton
            icon={<KeyRound size={15} />}
            label="Reset contraseña"
            onClick={onResetPassword}
            loading={isResettingPassword}
          />
          <ActionButton
            icon={<Lock size={15} />}
            label="Reset PIN wallet"
            onClick={onResetPin}
            loading={isResettingPin}
          />
          <ActionButton
            icon={<Ban size={15} />}
            label="Agregar a blacklist"
            onClick={onBlacklist}
            variant="danger"
          />
          <ActionButton
            icon={<Trash2 size={15} />}
            label="Eliminar usuario"
            onClick={onDelete}
            variant="danger"
          />
        </div>
      </div>
    </div>
  );
}

/* ── Copyable Field ────────────────────────────────────────── */

function CopyableField({label, value}: {label: string; value: string}) {
  const {copied, copy} = useCopyToClipboard();

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-(--color-surface-2)">
      <div className="flex items-center gap-2 min-w-0">
        <p className="text-xs text-(--color-text-tertiary) shrink-0">{label}:</p>
        <p className="text-sm font-mono text-(--color-text-primary) truncate">{value}</p>
      </div>
      <button
        type="button"
        onClick={() => copy(value)}
        className="shrink-0 p-1.5 rounded-md transition-colors cursor-pointer hover:bg-(--color-surface-3)"
        title={copied ? "Copiado" : `Copiar ${label}`}
      >
        {copied ? (
          <Check
            size={14}
            className="text-(--color-success)"
          />
        ) : (
          <Copy
            size={14}
            className="text-(--color-text-tertiary)"
          />
        )}
      </button>
    </div>
  );
}

/* ── Info Item ─────────────────────────────────────────────── */

function InfoItem({icon, label, value}: {icon: React.ReactNode; label: string; value: string}) {
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-(--color-surface-2)">
      <span className="text-(--color-text-tertiary) mt-0.5">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-(--color-text-tertiary)">{label}</p>
        <p className="text-sm font-medium text-(--color-text-primary) truncate">{value}</p>
      </div>
    </div>
  );
}

/* ── Action Button ─────────────────────────────────────────── */

function ActionButton({
  icon,
  label,
  onClick,
  loading,
  variant = "default",
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  loading?: boolean;
  variant?: "default" | "danger" | "warning" | "success";
}) {
  const variantClasses: Record<string, string> = {
    default: "text-(--color-text-secondary) hover:bg-(--color-surface-2) hover:text-(--color-text-primary)",
    danger: "text-(--color-danger) hover:bg-red-50",
    warning: "text-(--color-warning) hover:bg-amber-50",
    success: "text-(--color-success) hover:bg-emerald-50",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={[
        "flex items-center gap-2 px-3 py-2.5 rounded-lg border border-(--color-border) text-sm font-medium transition-colors",
        variantClasses[variant],
        loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
      ].join(" ")}
    >
      {loading ? (
        <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        icon
      )}
      {label}
    </button>
  );
}

/* ── Edit View ─────────────────────────────────────────────── */

interface EditViewProps {
  user: User;
  onBack: () => void;
  updateUser: ReturnType<typeof useUpdateUser>;
  onSuccess: () => void;
}

function EditView({user, onBack, updateUser, onSuccess}: EditViewProps) {
  const [form, setForm] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
  });

  const setField = (field: string, value: string) => setForm(prev => ({...prev, [field]: value}));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) return;

    updateUser.mutate({userId: user.id, body: {firstName: form.firstName, lastName: form.lastName}}, {onSuccess});
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
    >
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-(--color-accent) hover:underline self-start cursor-pointer"
      >
        ← Volver al detalle
      </button>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="edit-first"
            className="text-sm font-medium text-(--color-text)"
          >
            Nombre
          </label>
          <input
            id="edit-first"
            value={form.firstName}
            onChange={e => setField("firstName", e.target.value)}
            className="w-full bg-white border border-(--color-border) rounded-md py-2 px-3 text-sm text-(--color-text) outline-none focus:border-(--color-accent)"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="edit-last"
            className="text-sm font-medium text-(--color-text)"
          >
            Apellido
          </label>
          <input
            id="edit-last"
            value={form.lastName}
            onChange={e => setField("lastName", e.target.value)}
            className="w-full bg-white border border-(--color-border) rounded-md py-2 px-3 text-sm text-(--color-text) outline-none focus:border-(--color-accent)"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 justify-end mt-2">
        <BaseButton
          variant="secondary"
          size="md"
          type="button"
          onClick={onBack}
        >
          Cancelar
        </BaseButton>
        <BaseButton
          variant="primary"
          size="md"
          type="submit"
          loading={updateUser.isPending}
        >
          Guardar cambios
        </BaseButton>
      </div>
    </form>
  );
}

/* ── Blacklist View ────────────────────────────────────────── */

interface BlacklistViewProps {
  user: User;
  onBack: () => void;
  createBlacklist: ReturnType<typeof useCreateBlacklistEntry>;
  onSuccess: () => void;
}

function BlacklistView({user, onBack, createBlacklist, onSuccess}: BlacklistViewProps) {
  const [entryType, setEntryType] = useState<string>("email");
  const [reason, setReason] = useState("");

  const entryTypeOptions = [
    {value: "email", label: "Email"},
    {value: "full_name", label: "Nombre completo"},
    {value: "phone", label: "Teléfono"},
    {value: "id_document", label: "Documento"},
  ];

  const valueMap: Record<string, string> = {
    email: user.email,
    full_name: user.fullName,
    phone: user.phone ?? "",
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;

    createBlacklist.mutate(
      {
        entryType: entryType as "email" | "full_name" | "phone" | "id_document",
        value: valueMap[entryType] ?? "",
        source: "manual",
        reason,
      },
      {onSuccess}
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
    >
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-(--color-accent) hover:underline self-start cursor-pointer"
      >
        ← Volver al detalle
      </button>

      <div className="p-4 rounded-lg bg-red-50 border border-red-200">
        <div className="flex items-center gap-2 mb-2">
          <Ban
            size={16}
            className="text-(--color-danger)"
          />
          <p className="text-sm font-semibold text-(--color-danger)">Agregar a Blacklist</p>
        </div>
        <p className="text-xs text-(--color-text-secondary)">
          Esta acción agregará al usuario <strong>{user.fullName}</strong> a la lista negra del sistema.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="bl-type"
            className="text-sm font-medium text-(--color-text)"
          >
            Tipo de entrada
          </label>
          <select
            id="bl-type"
            value={entryType}
            onChange={e => setEntryType(e.target.value)}
            className="w-full bg-white border border-(--color-border) rounded-md py-2 pl-3 pr-8 text-sm text-(--color-text) outline-none focus:border-(--color-accent) appearance-none cursor-pointer"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 0.75rem center",
            }}
          >
            {entryTypeOptions.map(opt => (
              <option
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="bl-value"
            className="text-sm font-medium text-(--color-text)"
          >
            Valor
          </label>
          <input
            id="bl-value"
            value={valueMap[entryType] ?? ""}
            readOnly
            className="w-full bg-(--color-surface-2) border border-(--color-border) rounded-md py-2 px-3 text-sm text-(--color-text-secondary) cursor-not-allowed"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="bl-reason"
            className="text-sm font-medium text-(--color-text)"
          >
            Motivo
          </label>
          <textarea
            id="bl-reason"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Describa el motivo para agregar a blacklist..."
            rows={3}
            className="w-full bg-white border border-(--color-border) rounded-md py-2 px-3 text-sm text-(--color-text) outline-none focus:border-(--color-accent) resize-none placeholder:text-(--color-text-tertiary)"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 justify-end mt-2">
        <BaseButton
          variant="secondary"
          size="md"
          type="button"
          onClick={onBack}
        >
          Cancelar
        </BaseButton>
        <BaseButton
          variant="danger"
          size="md"
          type="submit"
          loading={createBlacklist.isPending}
          disabled={!reason.trim()}
        >
          Confirmar blacklist
        </BaseButton>
      </div>
    </form>
  );
}

/* ── Confirm Delete View ───────────────────────────────────── */

interface ConfirmDeleteViewProps {
  user: User;
  onBack: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

function ConfirmDeleteView({user, onBack, onConfirm, isDeleting}: ConfirmDeleteViewProps) {
  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-(--color-accent) hover:underline self-start cursor-pointer"
      >
        ← Volver al detalle
      </button>

      <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-center">
        <Trash2
          size={32}
          className="text-(--color-danger) mx-auto mb-3"
        />
        <p className="text-base font-semibold text-(--color-text-primary) mb-1">¿Eliminar usuario?</p>
        <p className="text-sm text-(--color-text-secondary)">
          Esta acción desactivará permanentemente la cuenta de <strong>{user.fullName}</strong> ({user.email}).
        </p>
      </div>

      <div className="flex items-center gap-3 justify-end mt-2">
        <BaseButton
          variant="secondary"
          size="md"
          onClick={onBack}
        >
          Cancelar
        </BaseButton>
        <BaseButton
          variant="danger"
          size="md"
          onClick={onConfirm}
          loading={isDeleting}
        >
          Sí, eliminar
        </BaseButton>
      </div>
    </div>
  );
}
